import { Graph } from "@hydrologiq/schemas/1.0.0/installationPage"

type GraphWithBatch = Graph & {
  batchGraphId?: string
  filter?: { role?: string; class?: string }
}

/**
 * Stable id for batch Timescale monitoring (unique per chart when multiple share the same field).
 */
export function getTimescaleBatchGraphId(graph: Graph): string {
  const field = graph.source.field
  const ext = graph as GraphWithBatch
  if (ext.batchGraphId) {
    return ext.batchGraphId
  }
  if (ext.filter?.role) {
    return `${field}__role_${ext.filter.role}`
  }
  if (ext.filter?.class) {
    return `${field}__class_${ext.filter.class}`
  }
  return field
}

type BatchChartRow = Record<string, unknown>

function chartRowHasNumericValue(row: BatchChartRow): boolean {
  for (const key of Object.keys(row)) {
    if (key === "timestamp" || key === "time") continue
    const v = row[key]
    if (typeof v === "number" && Number.isFinite(v)) return true
  }
  return false
}

/**
 * True if aggregated or breakdown contains at least one finite numeric sample
 * (used to hide a fuel or storage chart that has no data).
 */
export function batchPayloadHasNumericData(
  payload:
    | {
        aggregated?: { data: BatchChartRow[] }
        breakdown: { data: BatchChartRow[] }
      }
    | undefined
): boolean {
  if (!payload) return false
  if (payload.aggregated?.data?.some(chartRowHasNumericValue)) return true
  if (payload.breakdown.data.some(chartRowHasNumericValue)) return true
  return false
}

/** Graph classes that are hidden, each on its own, when the batch returned no data for them. */
const HIDDEN_WHEN_EMPTY_CLASSES = new Set(["fuel", "storage"])
/** Fields with no class filter that are hidden the same way (fuel is on a hybrid, not on a class). */
const HIDDEN_WHEN_EMPTY_FIELDS = new Set(["fuel_consumed"])

/**
 * Drops the fuel, storage and fuel consumed graphs that have nothing to draw. Every other graph is
 * kept, so a system without data still reads "Waiting for data" rather than a blank page.
 */
export function withoutEmptyTankGraphs<T extends { graph: Graph }>(
  entries: T[],
  payloadFor: (graph: Graph) => Parameters<typeof batchPayloadHasNumericData>[0]
): T[] {
  return entries.filter((entry) => {
    const graphClass = (entry.graph as GraphWithBatch).filter?.class
    const hideWhenEmpty =
      (graphClass !== undefined && HIDDEN_WHEN_EMPTY_CLASSES.has(graphClass)) ||
      HIDDEN_WHEN_EMPTY_FIELDS.has(entry.graph.source.field)
    if (!hideWhenEmpty) return true
    return batchPayloadHasNumericData(payloadFor(entry.graph))
  })
}
